'use client';

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Session } from 'next-auth';
import { useSession, SessionProvider } from 'next-auth/react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, MessageCircle, Target, Zap, Loader2 } from 'lucide-react';
import { createChatWithPrompt } from '../actions';
import { useRouter } from 'next/navigation';

// Typing animation hook
function useAnimatedTyping(text: string, speed: number = 50) {
	const [displayText, setDisplayText] = useState('');
	const [isTyping, setIsTyping] = useState(false);

	useEffect(() => {
		if (!text) return;
		
		setIsTyping(true);
		setDisplayText('');
		let index = 0;

		const interval = setInterval(() => {
			if (index < text.length) {
				setDisplayText(text.slice(0, index + 1));
				index++;
			} else {
				setIsTyping(false);
				clearInterval(interval);
			}
		}, speed);

		return () => clearInterval(interval);
	}, [text, speed]);

	return { displayText, isTyping };
}

// Interactive Hero section with full functionality
function InteractiveHeroSection({
	sessionRef,
	searchParams
}: {
	sessionRef: React.MutableRefObject<Session | null>;
	searchParams?: { domain?: string; goal?: string; ist?: string };
}) {
	const [inputValue, setInputValue] = useState('');
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [shouldAnimate, setShouldAnimate] = useState(false);
	const router = useRouter();

	// Prepare the domain text for animation
	const domainText = searchParams?.domain 
		? `I work at ${searchParams.domain}, find me some companies that could be my customers`
		: '';

	// Animated typing effect
	const { displayText, isTyping } = useAnimatedTyping(shouldAnimate ? domainText : '', 30);

	// Start animation when component mounts with domain
	useEffect(() => {
		if (domainText) {
			// Small delay to let the UI settle, then start typing animation
			const timer = setTimeout(() => {
				setShouldAnimate(true);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [domainText]);

	// Update input value as animation progresses
	useEffect(() => {
		if (shouldAnimate && displayText) {
			setInputValue(displayText);
		}
	}, [displayText, shouldAnimate]);

	// Auto-submit when typing animation completes
	useEffect(() => {
		if (shouldAnimate && !isTyping && displayText === domainText && domainText) {
			const timer = setTimeout(() => {
				handleSubmit();
			}, 1000); // Wait 1 second after typing completes, then submit
			return () => clearTimeout(timer);
		}
	}, [isTyping, displayText, domainText, shouldAnimate]);

	const handleSubmit = async () => {
		if (!inputValue.trim()) return;

		console.log('Starting lead analysis for:', inputValue);
		setIsAnalyzing(true);

		try {
			// Get siteId from session or use a default
			const siteId = sessionRef.current?.siteId || 'default-site';

			// Create chat with the initial prompt
			const result = await createChatWithPrompt(siteId, inputValue);

			// Smooth transition to chat page
			router.push(result.url);
		} catch (error) {
			console.error('Failed to create chat:', error);
			setIsAnalyzing(false);
		}
	};

	return (
		<section className="relative px-6 pt-20 pb-16 lg:px-8">
			<div className="mx-auto max-w-6xl text-center">
				<Badge className="mb-6 bg-blue-100 text-blue-700 hover:bg-blue-200">
					🧠 AI-Powered Lead Research + Verification
				</Badge>

				<h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-6xl lg:text-7xl">
					Own Your Go-to-Market,
					<br />
					<span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
						Find Leads from Any Source.
					</span>
				</h1>

				<p className="mt-6 text-lg sm:text-xl leading-7 sm:leading-8 text-gray-600 max-w-3xl mx-auto px-4 sm:px-0">
					The conversational spreadsheet that finds leads from unique data sources across
					the internet and native apps. Chat with your data, verify emails, enrich
					contacts, then export to your existing pipeline.
				</p>

				<motion.div
					className="mt-16 mx-auto max-w-2xl"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
					exit={{ opacity: 0, scale: 0.95, y: -20 }}
				>
					<div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8">
						<div className="space-y-6">
							<div className="relative group">
								<div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-sm transition-all duration-300"></div>
								<div className="relative flex gap-3">
									<Input
										type="text"
										placeholder="Tell me about your ideal customers and I'll find them for you..."
										value={inputValue}
										onChange={(e) => setInputValue(e.target.value)}
										onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
										className="flex-1 h-16 text-lg pl-6 pr-6 rounded-2xl border-2 border-gray-200 focus:border-blue-500 transition-all duration-300 bg-white shadow-inner"
										disabled={isAnalyzing}
									/>
									<Button
										onClick={handleSubmit}
										disabled={isAnalyzing || !inputValue.trim()}
										className="h-16 w-16 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 shadow-lg rounded-2xl flex-shrink-0 transition-all duration-200"
									>
										{isAnalyzing ? (
											<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
										) : (
											<TrendingUp className="h-6 w-6 text-white" />
										)}
									</Button>
								</div>
							</div>

							<div className="text-center space-y-2">
								<p className="text-sm text-gray-500 flex items-center justify-center gap-2">
									{isAnalyzing ? (
										<>
											<span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
											Creating your AI spreadsheet...
										</>
									) : (
										<>
											<MessageCircle className="w-4 h-4 text-blue-500" />
											Start a conversation to find leads from any source
										</>
									)}
								</p>
								<p className="text-sm font-semibold text-green-600 bg-green-50 px-4 py-2 rounded-full inline-block">
									✓ No Login Required
								</p>
							</div>
						</div>
					</div>
				</motion.div>

				{/* Feature highlights */}
				<div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
					<div className="text-center">
						<div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
							<Target className="w-6 h-6 text-blue-600" />
						</div>
						<h3 className="font-semibold text-gray-900 mb-2">Find Any Lead Source</h3>
						<p className="text-sm text-gray-600">
							AI searches unique data sources across the internet and native apps
						</p>
					</div>
					<div className="text-center">
						<div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
							<MessageCircle className="w-6 h-6 text-purple-600" />
						</div>
						<h3 className="font-semibold text-gray-900 mb-2">
							Conversational Spreadsheet
						</h3>
						<p className="text-sm text-gray-600">
							Chat with your data to enrich, verify, and organize leads
						</p>
					</div>
					<div className="text-center">
						<div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
							<Zap className="w-6 h-6 text-green-600" />
						</div>
						<h3 className="font-semibold text-gray-900 mb-2">Export to Pipeline</h3>
						<p className="text-sm text-gray-600">
							Built-in verification and export to Instantly, Smartlead, & more
						</p>
					</div>
				</div>
			</div>
		</section>
	);
}

// Static version that shows while session is loading
function StaticHeroSection() {
	return (
		<section className="relative px-6 pt-20 pb-16 lg:px-8">
			<div className="mx-auto max-w-6xl text-center">
				<Badge className="mb-6 bg-blue-100 text-blue-700 hover:bg-blue-200">
					🧠 AI-Powered Lead Research + Verification
				</Badge>

				<h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-6xl lg:text-7xl">
					Own Your Go-to-Market,
					<br />
					<span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
						Find Leads from Any Source.
					</span>
				</h1>

				<p className="mt-6 text-lg sm:text-xl leading-7 sm:leading-8 text-gray-600 max-w-3xl mx-auto px-4 sm:px-0">
					The conversational spreadsheet that finds leads from unique data sources across
					the internet and native apps. Chat with your data, verify emails, enrich
					contacts, then export to your existing pipeline.
				</p>

				<div className="mt-16 mx-auto max-w-2xl">
					<div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8">
						<div className="space-y-6">
							<div className="relative group">
								<div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-sm transition-all duration-300"></div>
								<div className="relative flex gap-3">
									<Input
										type="text"
										placeholder="Tell me about your ideal customers and I'll find them for you..."
										value=""
										disabled
										className="flex-1 h-16 text-lg pl-6 pr-6 rounded-2xl border-2 border-gray-200 transition-all duration-300 bg-gray-50 shadow-inner"
									/>
									<Button
										disabled
										className="h-16 w-16 bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg rounded-2xl flex-shrink-0 opacity-50"
									>
										<Loader2 className="h-6 w-6 text-white animate-spin" />
									</Button>
								</div>
							</div>

							<div className="text-center space-y-2">
								<p className="text-sm text-gray-500 flex items-center justify-center gap-2">
									<span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
									Initializing AI spreadsheet...
								</p>
								<p className="text-sm font-semibold text-green-600 bg-green-50 px-4 py-2 rounded-full inline-block">
									✓ No Login Required
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Feature highlights */}
				<div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
					<div className="text-center">
						<div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
							<Target className="w-6 h-6 text-blue-600" />
						</div>
						<h3 className="font-semibold text-gray-900 mb-2">Find Any Lead Source</h3>
						<p className="text-sm text-gray-600">
							AI searches unique data sources across the internet and native apps
						</p>
					</div>
					<div className="text-center">
						<div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
							<MessageCircle className="w-6 h-6 text-purple-600" />
						</div>
						<h3 className="font-semibold text-gray-900 mb-2">
							Conversational Spreadsheet
						</h3>
						<p className="text-sm text-gray-600">
							Chat with your data to enrich, verify, and organize leads
						</p>
					</div>
					<div className="text-center">
						<div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
							<Zap className="w-6 h-6 text-green-600" />
						</div>
						<h3 className="font-semibold text-gray-900 mb-2">Export to Pipeline</h3>
						<p className="text-sm text-gray-600">
							Built-in verification and export to Instantly, Smartlead, & more
						</p>
					</div>
				</div>
			</div>
		</section>
	);
}

// Main component following the nosemail pattern
function HeroSectionWithSession({ 
	initialSession, 
	searchParams 
}: { 
	initialSession: Session | null;
	searchParams?: { domain?: string; goal?: string; ist?: string };
}) {
	const { data: session, status } = useSession();
	const sessionRef = useRef<Session | null>(initialSession);

	useEffect(() => {
		if (!session) {
			return;
		}
		if (session && sessionRef.current?.siteId !== session?.siteId) {
			sessionRef.current = session;
		}
	}, [session, status]);

	// Show static version while session is loading, then interactive when ready
	if (!sessionRef.current || !sessionRef.current.siteId) {
		return <StaticHeroSection />;
	}

	return <InteractiveHeroSection sessionRef={sessionRef} searchParams={searchParams} />;
}

// Main export that wraps with SessionProvider
export function HeroSection({ 
	initialSession, 
	searchParams 
}: { 
	initialSession: Session | null;
	searchParams?: { domain?: string; goal?: string; ist?: string };
}) {
	return (
		<SessionProvider refetchOnWindowFocus={false} refetchInterval={0}>
			<HeroSectionWithSession initialSession={initialSession} searchParams={searchParams} />
		</SessionProvider>
	);
}
